// File Location: src/components/policy/PolicyHistory.jsx

import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  DollarSign,
  FileText,
  User,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  BarChart3,
  TrendingUp,
  Target,
  Award,
  RefreshCw,
  AlertTriangle,
  Shield,
  X,
} from "lucide-react";
import {
  getPolicyWriterReviewedApplications,
  getPolicyWriterPerformanceMetrics,
} from "../../services/policyWriterService";
import PolicyApplicationDetails from "./PolicyApplicationDetails";
import { useAuth } from "../../context/AuthContext";
import { exportPatientApplications } from "../../services/exportService";

const PolicyHistory = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showMetrics, setShowMetrics] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(10);

  // Filters
  const [filters, setFilters] = useState({
    searchTerm: "",
    status: "",
    dateFrom: "",
    dateTo: "",
    priority: "",
    insuranceCompany: "",
  });

  // Date range for metrics
  const [metricsDateRange, setMetricsDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split("T")[0], // First day of current month
    end: new Date().toISOString().split("T")[0], // Today
  });

  useEffect(() => {
    if (user?.uid) {
      loadApplications();
      loadMetrics();
    }
  }, [user, currentPage, filters]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const result = await getPolicyWriterReviewedApplications(
        user.uid,
        filters,
        currentPage,
        pageSize
      );
      if (result.success) {
        setApplications(result.applications);
        setTotal(result.total);
        setTotalPages(result.totalPages);
      }
    } catch (error) {
      console.error("Error loading applications:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      const result = await getPolicyWriterPerformanceMetrics(
        user.uid,
        metricsDateRange
      );
      if (result.success) {
        setMetrics(result.metrics);
      }
    } catch (error) {
      console.error("Error loading metrics:", error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadApplications(), loadMetrics()]);
    setRefreshing(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: "",
      status: "",
      dateFrom: "",
      dateTo: "",
      priority: "",
      insuranceCompany: "",
    });
    setCurrentPage(1);
  };

  const handleApplicationSelect = (application) => {
    setSelectedApplication(application);
  };

  const handleBackToHistory = () => {
    setSelectedApplication(null);
    loadApplications(); // Refresh data when coming back
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "pending_info":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4" />;
      case "rejected":
        return <XCircle className="h-4 w-4" />;
      case "pending_info":
        return <Clock className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority, emergency) => {
    if (emergency) return "bg-red-100 text-red-800 border-red-200";
    switch (priority) {
      case "urgent":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "high":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  const exportToCSV = () => {
    try {
      const result = exportPatientApplications(applications, 'policy_review_history');
      if (result.success) {
        alert(result.message);
      } else {
        alert('Export failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error exporting policy history:', error);
      alert('Failed to export policy history');
    }
  };

  // If an application is selected, show the details component
  if (selectedApplication) {
    return (
      <PolicyApplicationDetails
        applicationId={selectedApplication.applicationId}
        onBack={handleBackToHistory}
      />
    );
  }

  const renderMetrics = () => {
    if (!metrics) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Total Reviews */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Reviews</p>
              <p className="text-2xl font-bold text-blue-600">
                {metrics.totalReviewed}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Avg: {metrics.avgDailyReviews}/day
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <BarChart3 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Approval Rate */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Approval Rate</p>
              <p className="text-2xl font-bold text-green-600">
                {metrics.approvalRate}%
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {metrics.approved} of {metrics.totalReviewed} approved
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <Target className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Average Review Time */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Avg Review Time
              </p>
              <p className="text-2xl font-bold text-purple-600">
                {metrics.averageReviewTime}m
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Total: {Math.round(metrics.totalReviewTime / 60)}h
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Coverage Reviewed */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Coverage Reviewed
              </p>
              <p className="text-2xl font-bold text-teal-600">
                {formatAmount(metrics.totalCoverageReviewed)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Utilization: {metrics.coverageUtilizationRate}%
              </p>
            </div>
            <div className="bg-teal-100 p-3 rounded-lg">
              <TrendingUp className="h-6 w-6 text-teal-600" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFilters = () => (
    <div
      className={`transition-all duration-300 overflow-hidden ${
        showFilters ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
      }`}
    >
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Review Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All Status</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="pending_info">Pending Info</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange("priority", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All Priorities</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          {/* Insurance Company Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Insurance Company
            </label>
            <input
              type="text"
              placeholder="Filter by company"
              value={filters.insuranceCompany}
              onChange={(e) =>
                handleFilterChange("insuranceCompany", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Review Date From
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Review Date To
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
            >
              <X className="h-4 w-4" />
              <span>Clear</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderApplicationRow = (application) => {
    const policyVerification = application.workflow?.policyVerification;
    const reviewTime = policyVerification?.reviewTimeMinutes || 0;

    return (
      <tr
        key={application.id}
        className="bg-white hover:bg-gray-50 transition-colors"
      >
        <td className="px-6 py-4 whitespace-nowrap">
          <div>
            <div className="text-sm font-medium text-gray-900">
              {application.applicationId}
            </div>
            <div className="text-sm text-gray-500">
              {application.patient?.name}
            </div>
            <div className="flex items-center space-x-2 mt-1">
              {application.emergency && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Emergency
                </span>
              )}
              {application.priority && application.priority !== "normal" && (
                <span
                  className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                    application.priority,
                    application.emergency
                  )}`}
                >
                  {application.priority.charAt(0).toUpperCase() +
                    application.priority.slice(1)}
                </span>
              )}
            </div>
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className="bg-blue-100 rounded-full p-2 mr-3">
              <Building2 className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">
                {application.hospitalName}
              </div>
              <div className="text-sm text-gray-500">
                {application.medical?.treatmentType?.charAt(0).toUpperCase() +
                  application.medical?.treatmentType?.slice(1)}
              </div>
            </div>
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          <div>
            <div className="text-sm font-medium text-gray-900">
              {application.policy?.insuranceCompany}
            </div>
            <div className="text-sm text-gray-500 font-mono">
              {application.policy?.number}
            </div>
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          <div>
            <div className="text-sm font-medium text-gray-900">
              {formatAmount(application.policy?.coverageAmount)}
            </div>
            <div className="text-sm text-gray-500">
              Claim: {formatAmount(application.medical?.estimatedCost)}
            </div>
            {policyVerification?.approvedCoverageAmount && (
              <div className="text-sm text-green-600 font-medium">
                Approved:{" "}
                {formatAmount(policyVerification.approvedCoverageAmount)}
              </div>
            )}
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
            <div>
              <div className="text-sm text-gray-900">
                {formatDate(policyVerification?.verifiedAt)}
              </div>
              <div className="text-xs text-gray-500">
                Review time: {reviewTime}m
              </div>
            </div>
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <span
              className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(
                policyVerification?.status
              )}`}
            >
              {getStatusIcon(policyVerification?.status)}
              <span className="ml-1">
                {policyVerification?.status
                  ?.replace("_", " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </span>
            </span>
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-900">
            {policyVerification?.comments ? (
              <div
                className="max-w-xs truncate"
                title={policyVerification.comments}
              >
                {policyVerification.comments}
              </div>
            ) : (
              <span className="text-gray-400">No comments</span>
            )}
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <button
            onClick={() => handleApplicationSelect(application)}
            className="text-teal-600 hover:text-teal-800 transition-colors flex items-center space-x-1"
          >
            <Eye className="h-4 w-4" />
            <span>View</span>
          </button>
        </td>
      </tr>
    );
  };

  const renderPagination = () => (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>

      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing{" "}
            <span className="font-medium">
              {(currentPage - 1) * pageSize + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(currentPage * pageSize, total)}
            </span>{" "}
            of <span className="font-medium">{total}</span> results
          </p>
        </div>

        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              const pageNum = i + 1;
              const isActive = pageNum === currentPage;

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                    isActive
                      ? "z-10 bg-teal-50 border-teal-500 text-teal-600"
                      : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Policy Review History
          </h1>
          <p className="text-gray-600">
            Track your policy verification performance and history
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowMetrics(!showMetrics)}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
              showMetrics
                ? "bg-teal-50 border-teal-200 text-teal-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            <span>Metrics</span>
          </button>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            <span>Refresh</span>
          </button>

          <button
            onClick={exportToCSV}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Performance Metrics */}
      {showMetrics && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Performance Metrics
            </h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">
                  From:
                </label>
                <input
                  type="date"
                  value={metricsDateRange.start}
                  onChange={(e) =>
                    setMetricsDateRange((prev) => ({
                      ...prev,
                      start: e.target.value,
                    }))
                  }
                  className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">To:</label>
                <input
                  type="date"
                  value={metricsDateRange.end}
                  onChange={(e) =>
                    setMetricsDateRange((prev) => ({
                      ...prev,
                      end: e.target.value,
                    }))
                  }
                  className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <button
                onClick={loadMetrics}
                className="px-4 py-1 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 transition-colors"
              >
                Update
              </button>
            </div>
          </div>

          {renderMetrics()}

          {/* Additional Metrics */}
          {metrics && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-gray-200">
              <div className="text-center">
                <div className="bg-yellow-100 rounded-full p-3 w-fit mx-auto mb-2">
                  <Award className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {metrics.emergencyReviews}
                </div>
                <div className="text-sm text-gray-600">Emergency Cases</div>
              </div>

              <div className="text-center">
                <div className="bg-indigo-100 rounded-full p-3 w-fit mx-auto mb-2">
                  <FileText className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {metrics.complexCases}
                </div>
                <div className="text-sm text-gray-600">Complex Cases</div>
              </div>

              <div className="text-center">
                <div className="bg-green-100 rounded-full p-3 w-fit mx-auto mb-2">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {metrics.coverageUtilizationRate}%
                </div>
                <div className="text-sm text-gray-600">Avg Utilization</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-4 mb-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by application ID, patient name, or policy number..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
              showFilters
                ? "bg-teal-50 border-teal-200 text-teal-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${
                showFilters ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>

        {renderFilters()}
      </div>

      {/* Applications Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading review history...</p>
            </div>
          </div>
        ) : applications.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Application
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hospital
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Policy Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coverage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reviewed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Comments
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {applications.map(renderApplicationRow)}
                </tbody>
              </table>
            </div>
            {renderPagination()}
          </>
        ) : (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Review History
            </h3>
            <p className="text-gray-600 mb-4">
              {Object.values(filters).some((f) => f !== "")
                ? "No reviews match your current filters."
                : "You haven't reviewed any applications yet."}
            </p>
            {Object.values(filters).some((f) => f !== "") && (
              <button
                onClick={clearFilters}
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                Clear filters to show all reviews
              </button>
            )}
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {applications.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Period</p>
                <p className="text-2xl font-bold text-gray-900">
                  {applications.length}
                </p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  {
                    applications.filter(
                      (app) =>
                        app.workflow?.policyVerification?.status === "approved"
                    ).length
                  }
                </p>
              </div>
              <div className="bg-green-100 p-2 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Rejected</p>
                <p className="text-2xl font-bold text-red-600">
                  {
                    applications.filter(
                      (app) =>
                        app.workflow?.policyVerification?.status === "rejected"
                    ).length
                  }
                </p>
              </div>
              <div className="bg-red-100 p-2 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Amount</p>
                <p className="text-2xl font-bold text-teal-600">
                  {formatAmount(
                    applications.reduce(
                      (sum, app) =>
                        sum +
                        (app.workflow?.policyVerification
                          ?.approvedCoverageAmount || 0),
                      0
                    ) /
                      Math.max(
                        applications.filter(
                          (app) =>
                            app.workflow?.policyVerification
                              ?.approvedCoverageAmount > 0
                        ).length,
                        1
                      )
                  )}
                </p>
              </div>
              <div className="bg-teal-100 p-2 rounded-lg">
                <DollarSign className="h-5 w-5 text-teal-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Performance Insights */}
      {metrics && applications.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Performance Insights
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Review Speed Analysis
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    Fast Reviews (&lt;15 min)
                  </span>
                  <span className="text-sm font-medium text-green-600">
                    {
                      applications.filter(
                        (app) =>
                          (app.workflow?.policyVerification
                            ?.reviewTimeMinutes || 0) < 15
                      ).length
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    Standard Reviews (15-30 min)
                  </span>
                  <span className="text-sm font-medium text-yellow-600">
                    {
                      applications.filter((app) => {
                        const time =
                          app.workflow?.policyVerification?.reviewTimeMinutes ||
                          0;
                        return time >= 15 && time <= 30;
                      }).length
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    Detailed Reviews (&gt;30 min)
                  </span>
                  <span className="text-sm font-medium text-red-600">
                    {
                      applications.filter(
                        (app) =>
                          (app.workflow?.policyVerification
                            ?.reviewTimeMinutes || 0) > 30
                      ).length
                    }
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Coverage Analysis
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    High Value Claims (&gt;5L)
                  </span>
                  <span className="text-sm font-medium text-purple-600">
                    {
                      applications.filter(
                        (app) => (app.medical?.estimatedCost || 0) > 500000
                      ).length
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Emergency Cases</span>
                  <span className="text-sm font-medium text-red-600">
                    {applications.filter((app) => app.emergency).length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    Surgical Procedures
                  </span>
                  <span className="text-sm font-medium text-blue-600">
                    {
                      applications.filter(
                        (app) => app.medical?.treatmentType === "surgical"
                      ).length
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Score */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">
                Overall Performance Score
              </h4>
              <span className="text-2xl font-bold text-teal-600">
                {Math.round(
                  (metrics.approvalRate +
                    Math.max(10, 100 - metrics.averageReviewTime)) /
                    2
                )}
                /100
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-teal-500 to-green-500 h-3 rounded-full transition-all duration-500"
                style={{
                  width: `${Math.round(
                    (metrics.approvalRate +
                      Math.max(10, 100 - metrics.averageReviewTime)) /
                      2
                  )}%`,
                }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Based on approval rate ({metrics.approvalRate}%) and review
              efficiency ({Math.max(10, 100 - metrics.averageReviewTime)}/100)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyHistory;
